/**
 * O que a Meta conta sobre um Anúncio, traduzido para o nosso vocabulário.
 *
 * A Reducee espelha: nada aqui cria, edita ou pausa anúncio. O que se guarda é
 * o suficiente para a Administradora reconhecer a peça e saber onde mexer, e
 * nada além disso.
 */
/**
 * Traduz o `effective_status` da Meta para o Estado do anúncio.
 *
 * Usa `effective_status` porque `status` mente: medido nos 114 anúncios da
 * conta em 17/09/2026, 27 diziam `ACTIVE` e apenas 2 estavam mesmo no ar. A
 * diferença são anúncios ligados dentro de Campanha de anúncios ou Conjunto de
 * anúncios desligado — no Gerenciador eles aparecem parados, e a parede
 * precisa concordar com o Gerenciador.
 *
 * Valor que não se conhece vira `DESCONHECIDO`, e nunca `PAUSADO`. A Meta
 * documenta mais estados do que os cinco observados, e inventa outros sem
 * avisar; dizer "pausado" sobre um valor novo afirmaria que alguém desligou o
 * anúncio, que é exatamente a conclusão errada a tirar. `DESCONHECIDO` fica
 * fora do ar no filtro — o cuidadoso é não prometer veiculação —, mas se
 * declara ignorante na tela em vez de culpar alguém.
 */
export function interpretarEstadoDoAnuncio(efetivo) {
    switch ((efetivo ?? '').trim().toUpperCase()) {
        case 'ACTIVE':
            return 'NO_AR';
        case 'PAUSED':
            return 'PAUSADO';
        case 'CAMPAIGN_PAUSED':
            return 'CAMPANHA_PAUSADA';
        case 'ADSET_PAUSED':
            return 'CONJUNTO_PAUSADO';
        case 'DISAPPROVED':
            return 'REPROVADO';
        case 'WITH_ISSUES':
            return 'COM_PROBLEMA';
        case 'PENDING_REVIEW':
        case 'IN_PROCESS':
        case 'PREAPPROVED':
        case 'PENDING_BILLING_INFO':
            return 'EM_ANALISE';
        case 'ARCHIVED':
        case 'DELETED':
            return 'ARQUIVADO';
        default:
            return 'DESCONHECIDO';
    }
}
/**
 * Se o Anúncio conta como "No ar" no filtro que abre a parede.
 *
 * Só `NO_AR` conta. Estado que não se reconhece fica de fora: prometer
 * veiculação sobre um valor que ninguém interpretou é a mentira mais cara das
 * duas, porque manda a Administradora esperar Lead de um anúncio parado.
 */
export function anuncioEstaNoAr(estado) {
    return estado === 'NO_AR';
}
/**
 * Os dois modos da parede.
 *
 * Nove estados dariam nove abas para uma pergunta que tem duas respostas: o
 * que está veiculando agora, e o que não está. O motivo de não estar continua
 * escrito em cada cartão, que é onde ele ajuda — a lista serve para escolher
 * o que olhar, não para classificar.
 */
/**
 * As janelas de desempenho que a parede oferece.
 *
 * Janela fixa, e não intervalo livre: a tela lê uma cópia que o worker
 * espelhou antes, e data qualquer obrigaria a perguntar à Meta na abertura —
 * que é justamente o que a cópia existe para evitar.
 *
 * Medido na conta em 18/09/2026: 7 dias trazem 4 anúncios com gasto, 30 dias
 * trazem 19, 90 dias trazem 24, e a vida inteira traz 126. É essa diferença
 * que faz a escolha valer a pena — "o que está rendendo agora" e "o que já
 * rendeu" são perguntas diferentes.
 */
export const JANELAS_DE_DESEMPENHO = [
    'ULTIMOS_7_DIAS',
    'ULTIMOS_30_DIAS',
    'ULTIMOS_90_DIAS',
    'VIDA_INTEIRA',
];
/** A janela que a parede abre: a pergunta usual é sobre o mês corrente. */
export const JANELA_PADRAO = 'ULTIMOS_30_DIAS';
/**
 * O `date_preset` correspondente na Graph API.
 *
 * Mora aqui, e não no cliente da Meta, porque é a tradução entre o nosso
 * vocabulário e o deles — o mesmo lugar onde `interpretarEstadoDoAnuncio`
 * traduz `effective_status`.
 */
export const PERIODO_DA_META = {
    ULTIMOS_7_DIAS: 'last_7d',
    ULTIMOS_30_DIAS: 'last_30d',
    ULTIMOS_90_DIAS: 'last_90d',
    VIDA_INTEIRA: 'maximum',
};
/**
 * Quantos Anúncios a parede mostra por vez.
 *
 * A conta tem 114 peças e quase todas paradas: despejar as 112 de uma vez faz
 * o navegador baixar 112 imagens para a Administradora olhar as primeiras.
 */
export const ANUNCIOS_POR_PAGINA = 25;
export const FILTROS_DE_ANUNCIO = ['NO_AR', 'FORA_DO_AR'];
/**
 * Domínios de onde se aceita baixar imagem de anúncio.
 *
 * Esta lista é um controle de segurança, e não configuração. O worker baixa uma
 * URL que veio **dentro da resposta da Meta** e publica esses bytes num bucket
 * público servido pelo nosso domínio: sem lista, uma resposta adulterada faria
 * o worker buscar `http://169.254.169.254/` ou `http://redis:6379` — vizinhos
 * dele na rede do Swarm — e o corpo da resposta ficaria legível na internet, no
 * nosso endereço. É SSRF com canal de saída publicado.
 *
 * Saiu de medição, e não de suposição: nos 114 anúncios da conta, nos dois
 * campos de URL que a Meta devolve (`image_url` e `thumbnail_url`), **todo**
 * endereço cai em `fbcdn.net`.
 *
 * É sufixo, e nunca hostname exato. Os observados foram
 * `external.frec15-1.fna.fbcdn.net` e `scontent.frec15-1.fna.fbcdn.net`, e
 * `frec15` é o ponto de presença de Recife — de onde se mediu. A VPS sai por
 * outro, e um hostname fixo funcionaria na máquina de quem escreveu e falharia
 * em produção, com a parede vazia e nenhum erro que explicasse.
 */
/**
 * Formato de uma conta de anúncios da Meta.
 *
 * Mora aqui, e não em cada borda, porque já esteve escrito em dois lugares com
 * números diferentes: a api exigia cinco dígitos e o cliente aceitava um. Uma
 * conta de três dígitos passava na tela e só falhava no worker, horas depois,
 * num log que ninguém estava lendo.
 *
 * Só dígitos: o prefixo `act_` é da URL e não do identificador. Cinco no piso
 * porque a conta medida tem quinze, e nenhuma conta real é curta assim; trinta
 * e dois no teto porque é caminho de URL, não texto livre.
 */
export const FORMATO_DA_CONTA_DE_ANUNCIOS = /^[0-9]{5,32}$/;
export const DOMINIOS_DE_IMAGEM_DA_META = ['fbcdn.net'];
/**
 * Confere se o endereço da imagem pode ser buscado, sem ir à rede.
 *
 * Duas exigências, e as duas importam. `https` porque o corpo vira conteúdo
 * público no nosso domínio, e em texto claro qualquer um no caminho escolhe o
 * que vamos publicar. E o domínio, pela lista acima.
 *
 * A comparação exige o ponto antes do sufixo: `fbcdn.net.atacante.com` não
 * termina em `.fbcdn.net`, e é essa a armadilha que um `includes` deixaria
 * passar.
 *
 * Endereço que não passa não é defeito nem exceção: é anúncio que fica sem
 * imagem, e o resto da sincronização segue. Recusar aqui custa uma miniatura;
 * não recusar custa o domínio.
 */
export function enderecoDeImagemPermitido(endereco) {
    let url;
    try {
        url = new URL(endereco);
    }
    catch {
        return false;
    }
    if (url.protocol !== 'https:')
        return false;
    const host = url.hostname.toLowerCase();
    return DOMINIOS_DE_IMAGEM_DA_META.some((dominio) => host === dominio || host.endsWith(`.${dominio}`));
}
/**
 * Tipos de imagem que se aceita publicar, com a assinatura de bytes de cada um.
 *
 * O cabeçalho `Content-Type` da resposta não decide: quem serve a imagem depois
 * somos nós, no nosso domínio, e um `text/html` gravado ali vira página de
 * phishing hospedada pela Reducee. O tipo que vai para o bucket é o que estes
 * bytes provam, e não o que a resposta afirmou.
 *
 * Medido na amostra da conta: `image/jpeg` em todas, com a mágica `ff d8 ff`.
 * Os outros três estão aqui porque a Meta serve peça em PNG, GIF e WebP, e
 * recusá-los deixaria anúncio sem imagem sem motivo.
 */
const ASSINATURAS = [
    { tipo: 'image/jpeg', bytes: [0xff, 0xd8, 0xff] },
    { tipo: 'image/png', bytes: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] },
    { tipo: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },
];
/** WebP é `RIFF....WEBP`: quatro bytes, quatro de tamanho, e mais quatro. */
const RIFF = [0x52, 0x49, 0x46, 0x46];
const WEBP = [0x57, 0x45, 0x42, 0x50];
function comecaCom(bytes, prefixo, desde = 0) {
    if (bytes.length < desde + prefixo.length)
        return false;
    return prefixo.every((b, i) => bytes[desde + i] === b);
}
/**
 * Descobre o tipo da imagem pelos próprios bytes.
 *
 * Nulo quer dizer "isto não é imagem que se reconheça", e quem chama trata como
 * anúncio sem imagem. Nunca adivinhar: gravar bytes desconhecidos com um tipo
 * inventado é publicar conteúdo arbitrário no nosso domínio com a nossa
 * garantia em cima.
 */
export function tipoDaImagem(bytes) {
    for (const assinatura of ASSINATURAS) {
        if (comecaCom(bytes, assinatura.bytes))
            return assinatura.tipo;
    }
    if (comecaCom(bytes, RIFF) && comecaCom(bytes, WEBP, 8))
        return 'image/webp';
    return null;
}
/**
 * Se um endereço IP pertence à própria máquina, à rede interna ou a uma faixa
 * reservada.
 *
 * É a rede embaixo da lista de domínios, e não um substituto dela. A lista
 * responde "este host é da Meta?"; isto responde "para onde esse nome aponta
 * de verdade?". Os dois juntos é que barram o nome legítimo que resolve para
 * dentro, e o worker do Swarm tem Postgres, Redis e a api ao alcance.
 *
 * `169.254.0.0/16` está aqui com nome próprio: é a faixa dos serviços de
 * metadados de nuvem, e o alvo preferido de SSRF justamente porque devolve
 * credencial a quem perguntar sem autenticação.
 *
 * **Limite conhecido, e é honesto declará-lo:** conferir aqui e deixar o
 * `fetch` resolver o nome outra vez depois abre uma janela — um registro de TTL
 * curto pode trocar de resposta entre a conferência e a conexão. Fechá-la
 * exigiria conectar ao IP já resolvido, com máquinas a mais no caminho. Para
 * esta superfície não compensa: quem controlasse o DNS de `fbcdn.net` visto
 * daqui já teria a máquina, e o SSRF seria o menor dos problemas.
 */
export function ipEhInterno(ip) {
    const endereco = ip.trim().toLowerCase();
    if (endereco === '')
        return true;
    // IPv4 mapeado em IPv6 (`::ffff:127.0.0.1`) é o disfarce mais comum: sem
    // desembrulhar, o teste de IPv6 não reconhece a faixa e o de IPv4 nem roda.
    const mapeado = /^::ffff:(\d+\.\d+\.\d+\.\d+)$/.exec(endereco);
    if (mapeado?.[1])
        return ipEhInterno(mapeado[1]);
    if (endereco.includes(':'))
        return ipv6EhInterno(endereco);
    return ipv4EhInterno(endereco);
}
function ipv4EhInterno(endereco) {
    const partes = endereco.split('.');
    if (partes.length !== 4)
        return true;
    const octetos = partes.map((parte) => Number(parte));
    if (octetos.some((n) => !Number.isInteger(n) || n < 0 || n > 255))
        return true;
    const [a, b] = octetos;
    if (a === 0)
        return true; // "esta rede"
    if (a === 10)
        return true; // privada
    if (a === 127)
        return true; // a própria máquina
    if (a === 169 && b === 254)
        return true; // metadados de nuvem
    if (a === 172 && b >= 16 && b <= 31)
        return true; // privada
    if (a === 192 && b === 0)
        return true; // reservada pela IETF
    if (a === 192 && b === 168)
        return true; // privada
    if (a === 198 && (b === 18 || b === 19))
        return true; // teste de desempenho
    if (a === 100 && b >= 64 && b <= 127)
        return true; // CGNAT
    if (a >= 224)
        return true; // multicast e reservada
    return false;
}
function ipv6EhInterno(endereco) {
    const semZona = endereco.split('%')[0] ?? '';
    if (semZona === '::' || semZona === '::1')
        return true; // indefinido, própria máquina
    const primeiro = semZona.split(':')[0] ?? '';
    if (primeiro === '')
        return true; // qualquer outro `::alguma-coisa`
    const grupo = Number.parseInt(primeiro.padStart(4, '0'), 16);
    if (!Number.isInteger(grupo))
        return true;
    if ((grupo & 0xfe00) === 0xfc00)
        return true; // fc00::/7, local único
    if ((grupo & 0xffc0) === 0xfe80)
        return true; // fe80::/10, enlace local
    if ((grupo & 0xff00) === 0xff00)
        return true; // ff00::/8, multicast
    return false;
}
//# sourceMappingURL=anuncios-meta.js.map